/**
 * The one section card every HELM tab uses (A7Y-4407): the same chrome,
 * padding, h3 heading and table gutter, so Today, Inbox, Trends and Runs line
 * up. Tables inside pass `HELM_TABLE_GUTTER` so they bleed to the card edge
 * at the card's own padding.
 */
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge, Subheading, Text } from '@a7y/tailwind-catalyst';

export const HELM_TABLE_GUTTER = '[--gutter:--spacing(4)] sm:[--gutter:--spacing(6)]';

export function HelmSection({
  title,
  count,
  critical = false,
  summary,
  lineOn = 'header',
  children,
}: {
  title: string;
  count?: number | string;
  critical?: boolean;
  summary?: ReactNode;
  /** Where this section's own card line is marked: on the header when item
   * rows follow (each row is its own line), on the whole section when the
   * summary is all there is (its words live in the body), or nowhere for
   * body notes, which have no summary line. */
  lineOn?: 'header' | 'section' | 'none';
  children?: ReactNode;
}) {
  return (
    <section
      data-testid={lineOn === 'section' ? 'helm-card-line' : undefined}
      className="min-w-0 rounded-xl bg-white p-4 shadow-xs sm:p-6 ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10"
    >
      <header
        data-testid={lineOn === 'header' ? 'helm-card-line' : undefined}
        className="flex flex-wrap items-center gap-x-3 gap-y-1"
      >
        {critical && <AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-red-500" />}
        <Subheading level={3}>{title}</Subheading>
        {count !== undefined && <Badge color={critical ? 'red' : 'zinc'}>{count}</Badge>}
        {summary && (
          <Text className="basis-full text-zinc-500 sm:ml-auto sm:basis-auto dark:text-zinc-400">
            {summary}
          </Text>
        )}
      </header>
      {children && <div className="mt-4">{children}</div>}
    </section>
  );
}

export function HelmEmpty({ children }: { children: ReactNode }) {
  return (
    <Text className="text-zinc-500 first-letter:uppercase dark:text-zinc-400">{children}</Text>
  );
}
